import { TypesTimeZones } from '@zerodep/types.timezones';

export type ReebeloB2bStoreT = 'reebelo-b2b-dev' | 'reebelo-b2b';
export type ReebeloStoreT =
  | 'reebelo-dev'
  | 'reebelo-au'
  | 'reebelo-kr'
  | 'reebelo-nz'
  | 'quista'
  | 'reebelo-tw'
  | 'reebelo-us'
  | 'reebelo-ca'
  | 'reebelo-uk'
  | 'reebelo-fr'
  | 'reebelo-de'
  | 'reebelo-es'
  | 'reebelo-it'
  | 'reebelo-se'
  | 'reebelo-ie'
  | 'reebelo-nl'
  | 'reebelo-at'
  | 'reebelo-be'
  | 'reebelo-pt'
  | 'reebelo-fi';

export enum ReebeloB2bConditions {
  A = 'A',
  B = 'B',
  C = 'C',
  D = 'D',
  E = 'E',
}
// Good & Acceptable overlap
export enum ReebeloConditions {
  BrandNew = 'Brand New',
  LikeNew = 'Like New',
  Premium = 'Premium',
  Pristine = 'Pristine',
  Excellent = 'Excellent',
  Good = 'Good',
  Acceptable = 'Acceptable',
}

export type ReebeloMappingConditions =
  | ReebeloConditions.BrandNew
  | ReebeloConditions.LikeNew
  | ReebeloConditions.Pristine
  | ReebeloConditions.Good
  | ReebeloConditions.Acceptable;

export const ReebeloConditionsShortHand: Record<
  ReebeloMappingConditions,
  string
> = {
  [ReebeloConditions.BrandNew]: '1',
  [ReebeloConditions.LikeNew]: '2',
  [ReebeloConditions.Pristine]: '3',
  [ReebeloConditions.Good]: '4',
  [ReebeloConditions.Acceptable]: '5',
};
export const ReebeloConditionsShortHandReverse: Record<
  string,
  ReebeloMappingConditions
> = {
  '1': ReebeloConditions.BrandNew,
  '2': ReebeloConditions.LikeNew,
  '3': ReebeloConditions.Pristine,
  '4': ReebeloConditions.Good,
  '5': ReebeloConditions.Acceptable,
};
// We keep this for subsequent stores
export const REEBELO_CONDITIONS = (store: ReebeloStoreT) => [
  ReebeloConditions.BrandNew,
  ReebeloConditions.LikeNew,
  ReebeloConditions.Good,
  ReebeloConditions.Acceptable,
];

export const REEBELO_B2B_STORES: ReebeloB2bStoreT[] =
  process.env.STAGE === 'prod' || process.env.STAGE === 'test'
    ? ['reebelo-b2b']
    : ['reebelo-b2b-dev'];

export const REEBELO_STORES: ReebeloStoreT[] =
  process.env.STAGE === 'prod' || process.env.STAGE === 'test'
    ? [
        'quista',
        'reebelo-au',
        'reebelo-nz',
        'reebelo-kr',
        'reebelo-tw',
        'reebelo-us',
        'reebelo-ca',
        'reebelo-uk',
        'reebelo-fr',
        'reebelo-de',
        'reebelo-es',
        'reebelo-it',
        'reebelo-se',
        'reebelo-ie',
        'reebelo-nl',
        'reebelo-at',
        'reebelo-be',
        'reebelo-pt',
        'reebelo-fi',
      ]
    : ['reebelo-dev'];

const REEBELO_BLACKLISTED_STORES = ['reebelo-kr', 'reebelo-tw'];

export const REEBELO_LIVE_STORES = REEBELO_STORES.filter(
  (store) => !REEBELO_BLACKLISTED_STORES.includes(store),
);

export type ReebeloCurrencies =
  | 'AUD'
  | 'HKD'
  | 'MYR'
  | 'SGD'
  | 'NZD'
  | 'TWD'
  | 'KRW'
  | 'USD'
  | 'CAD'
  | 'GBP'
  | 'EUR'
  | 'SEK';

export const REEBELO_CURRENCY: Record<
  ReebeloStoreT | ReebeloB2bStoreT,
  ReebeloCurrencies
> = {
  'reebelo-au': 'AUD',
  'reebelo-nz': 'NZD',
  quista: 'SGD',
  'reebelo-tw': 'TWD',
  'reebelo-dev': 'SGD',
  'reebelo-kr': 'KRW',
  'reebelo-us': 'USD',
  'reebelo-ca': 'CAD',
  'reebelo-uk': 'GBP',
  'reebelo-fr': 'EUR',
  'reebelo-de': 'EUR',
  'reebelo-es': 'EUR',
  'reebelo-it': 'EUR',
  'reebelo-se': 'SEK',
  'reebelo-ie': 'EUR',
  'reebelo-nl': 'EUR',
  'reebelo-at': 'EUR',
  'reebelo-be': 'EUR',
  'reebelo-pt': 'EUR',
  'reebelo-fi': 'EUR',
  'reebelo-b2b': 'USD',
  'reebelo-b2b-dev': 'USD',
};

/**
 * Stores a vendor can cross-list into from another store in the same group.
 *
 * Every member must share a currency: vendor details are projected between
 * stores in this group verbatim, and `commission.subscriptionFee` is a bare
 * amount with no currency attached, so a mixed-currency group would silently
 * copy the wrong money. `EU_STORES have a single currency` in the tests here
 * enforces that.
 *
 * `reebelo-se` is intentionally NOT a member: it trades in SEK, not EUR
 * (RT-17307).
 */
export const EU_STORES: ReebeloStoreT[] = [
  'reebelo-fr',
  'reebelo-de',
  'reebelo-es',
  'reebelo-it',
  'reebelo-ie',
  'reebelo-nl',
  'reebelo-at',
  'reebelo-be',
  'reebelo-pt',
  'reebelo-fi',
];

/**
 * The stores an admin order view spans. An EU country view (e.g. `reebelo-fr#*`)
 * covers every EU store, so ops monitor all EU orders in one place; it follows
 * `EU_STORES`, so a store joins once it can be cross-listed into. Undefined for
 * seller keys, the global `*#*` view and non-EU country views, which keep their
 * existing single-store scope.
 */
export const getAdminOrderStores = (
  vendor: string,
): ReebeloStoreT[] | undefined => {
  const [store, vendorName] = vendor.split('#') as [ReebeloStoreT, string];

  if (vendorName !== '*' || !EU_STORES.includes(store)) {
    return undefined;
  }

  return EU_STORES;
};

export const REEBELO_COUNTRY_CODE: Record<
  ReebeloStoreT | ReebeloB2bStoreT,
  string
> = {
  'reebelo-au': 'AU',
  'reebelo-nz': 'NZ',
  quista: 'SG',
  'reebelo-dev': 'DEV',
  'reebelo-tw': 'TW',
  'reebelo-kr': 'KR',
  'reebelo-us': 'US',
  'reebelo-ca': 'CA',
  'reebelo-uk': 'GB',
  'reebelo-fr': 'FR',
  'reebelo-de': 'DE',
  'reebelo-es': 'ES',
  'reebelo-it': 'IT',
  'reebelo-se': 'SE',
  'reebelo-ie': 'IE',
  'reebelo-nl': 'NL',
  'reebelo-at': 'AT',
  'reebelo-be': 'BE',
  'reebelo-pt': 'PT',
  'reebelo-fi': 'FI',
  'reebelo-b2b': 'B2B',
  'reebelo-b2b-dev': 'B2B-DEV',
};

export const REEBELO_COUNTRY_TO_MARKET_MAPPINGS = {
  SG: 'quista',
  US: 'reebelo-us',
  AU: 'reebelo-au',
  DEV: 'reebelo-dev',
  NZ: 'reebelo-nz',
  CA: 'reebelo-ca',
  GB: 'reebelo-uk',
  FR: 'reebelo-fr',
  DE: 'reebelo-de',
  ES: 'reebelo-es',
  IT: 'reebelo-it',
  SE: 'reebelo-se',
  IE: 'reebelo-ie',
  NL: 'reebelo-nl',
  AT: 'reebelo-at',
  BE: 'reebelo-be',
  PT: 'reebelo-pt',
  FI: 'reebelo-fi',
};
export const REEBELO_COUNTRY: Record<ReebeloStoreT | ReebeloB2bStoreT, string> =
  {
    'reebelo-au': 'Australia',
    'reebelo-nz': 'New Zealand',
    quista: 'Singapore',
    'reebelo-dev': 'Singapore',
    'reebelo-tw': 'Taiwan',
    'reebelo-kr': 'Korea',
    'reebelo-us': 'United States of America',
    'reebelo-ca': 'Canada',
    'reebelo-uk': 'United Kingdom',
    'reebelo-fr': 'France',
    'reebelo-de': 'Germany',
    'reebelo-es': 'Spain',
    'reebelo-it': 'Italy',
    'reebelo-se': 'Sweden',
    'reebelo-ie': 'Ireland',
    'reebelo-nl': 'Netherlands',
    'reebelo-at': 'Austria',
    'reebelo-be': 'Belgium',
    'reebelo-pt': 'Portugal',
    'reebelo-fi': 'Finland',
    'reebelo-b2b': 'Reebelo B2B',
    'reebelo-b2b-dev': 'Reebelo B2B',
  };
export const REEBELO_DOMAINS: Record<ReebeloStoreT | ReebeloB2bStoreT, string> =
  {
    'reebelo-au':
      process.env.STAGE !== 'prod' ? 'au.reebelo.blue' : 'reebelo.com.au',
    quista: process.env.STAGE !== 'prod' ? 'sg.reebelo.blue' : 'reebelo.sg',
    'reebelo-nz':
      process.env.STAGE !== 'prod' ? 'nz.reebelo.blue' : 'reebelo.co.nz',
    'reebelo-tw': 'reebelo.com.tw',
    'reebelo-kr': 'reebelo.co.kr',
    'reebelo-dev': 'reebelo.blue',
    'reebelo-us':
      process.env.STAGE !== 'prod' ? 'us.reebelo.blue' : 'reebelo.com',
    'reebelo-ca':
      process.env.STAGE !== 'prod' ? 'ca.reebelo.blue' : 'reebelo.ca',
    'reebelo-uk':
      process.env.STAGE !== 'prod' ? 'uk.reebelo.blue' : 'reebelo.co.uk',
    'reebelo-fr':
      process.env.STAGE !== 'prod' ? 'fr.reebelo.blue' : 'reebelo.fr',
    'reebelo-de':
      process.env.STAGE !== 'prod' ? 'de.reebelo.blue' : 'reebelo.de',
    'reebelo-es':
      process.env.STAGE !== 'prod' ? 'es.reebelo.blue' : 'reebelo.es',
    'reebelo-it':
      process.env.STAGE !== 'prod' ? 'it.reebelo.blue' : 'reebelo.it',
    'reebelo-se':
      process.env.STAGE !== 'prod' ? 'se.reebelo.blue' : 'reebelo.se',
    'reebelo-ie':
      process.env.STAGE !== 'prod' ? 'ie.reebelo.blue' : 'reebelo.ie',
    'reebelo-nl':
      process.env.STAGE !== 'prod' ? 'nl.reebelo.blue' : 'reebelo.nl',
    'reebelo-at':
      process.env.STAGE !== 'prod' ? 'at.reebelo.blue' : 'reebelo.at',
    'reebelo-be':
      process.env.STAGE !== 'prod' ? 'be.reebelo.blue' : 'reebelo.be',
    'reebelo-pt':
      process.env.STAGE !== 'prod' ? 'pt.reebelo.blue' : 'reebelo.pt',
    'reebelo-fi':
      process.env.STAGE !== 'prod' ? 'fi.reebelo.blue' : 'reebelo.fi',
    'reebelo-b2b': 'reebelo-wholesale.com',
    'reebelo-b2b-dev': 'b2b.reebelo.blue',
  };

export const REEBELO_TZ_NAME: Record<
  ReebeloStoreT | ReebeloB2bStoreT,
  TypesTimeZones
> = {
  'reebelo-au': 'Australia/Melbourne',
  quista: 'Asia/Singapore',
  'reebelo-nz': 'Pacific/Auckland',
  'reebelo-tw': 'Asia/Singapore',
  'reebelo-dev': 'Asia/Singapore',
  'reebelo-kr': 'Asia/Seoul',
  'reebelo-us': 'America/Los_Angeles',
  'reebelo-ca': 'America/Toronto',
  'reebelo-uk': 'Europe/London',
  'reebelo-fr': 'Europe/Paris',
  'reebelo-de': 'Europe/Berlin',
  'reebelo-es': 'Europe/Madrid',
  'reebelo-it': 'Europe/Rome',
  'reebelo-se': 'Europe/Stockholm',
  'reebelo-ie': 'Europe/Dublin',
  'reebelo-nl': 'Europe/Amsterdam',
  'reebelo-at': 'Europe/Vienna',
  'reebelo-be': 'Europe/Brussels',
  'reebelo-pt': 'Europe/Lisbon',
  'reebelo-fi': 'Europe/Helsinki',
  'reebelo-b2b': 'America/Los_Angeles',
  'reebelo-b2b-dev': 'America/Yellowknife', // 'America/Los_Angeles' revert back on 12 Mar 2023
};

export const TIMEZONE_ABBREVIATIONS: Record<string, string> = {
  'Pacific/Auckland': 'NZDT',
  'Australia/Melbourne': 'AEST',
  'Asia/Singapore': 'SGT',
  'Asia/Seoul': 'KST',
  'America/Yellowknife': 'PDT',
  'America/Toronto': 'EDT',
  'Pacific/Fiji': 'NZST',
  'Australia/Brisbane': 'AEST',
  'America/Los_Angeles': 'PDT',
  'Europe/London': 'GMT',
  'Europe/Paris': 'CET',
  'Europe/Berlin': 'CET',
  'Europe/Madrid': 'CET',
  'Europe/Rome': 'CET',
  'Europe/Stockholm': 'CET',
  'Europe/Dublin': 'GMT',
  'Europe/Amsterdam': 'CET',
  'Europe/Vienna': 'CET',
  'Europe/Brussels': 'CET',
  'Europe/Lisbon': 'WET',
  'Europe/Helsinki': 'EET',
};

// Can be extended to be 'kr' | 'zh' ...etc
// https://www.loc.gov/standards/iso639-2/php/code_list.php
export type ReebeloLanguage =
  | 'en'
  | 'ko'
  | 'zh'
  | 'fr'
  | 'de'
  | 'es'
  | 'it'
  | 'sv'
  | 'nl'
  | 'pt'
  | 'fi';
export const REEBELO_STORE_TO_LANGUAGE: Record<
  ReebeloStoreT | ReebeloB2bStoreT,
  ReebeloLanguage
> = {
  'reebelo-au': 'en',
  quista: 'en',
  'reebelo-nz': 'en',
  'reebelo-tw': 'zh',
  'reebelo-dev': 'en',
  'reebelo-kr': 'ko',
  'reebelo-us': 'en',
  'reebelo-ca': 'en',
  'reebelo-uk': 'en',
  'reebelo-fr': 'fr',
  'reebelo-de': 'de',
  'reebelo-es': 'es',
  'reebelo-it': 'it',
  'reebelo-se': 'sv',
  'reebelo-ie': 'en',
  'reebelo-nl': 'nl',
  'reebelo-at': 'de',
  'reebelo-be': 'fr',
  'reebelo-pt': 'pt',
  'reebelo-fi': 'fi',
  'reebelo-b2b': 'en',
  'reebelo-b2b-dev': 'en',
};
export type SegmentLanguageTag =
  | 'en'
  | 'fr'
  | 'de'
  | 'es'
  | 'it'
  | 'sv'
  | 'nl'
  | 'pt'
  | 'fi';

// Fixed from a hardcoded 'reebelo-fr' ternary to a proper store->language lookup:
// a ternary would have silently misclassified reebelo-de/reebelo-es (or any future
// non-English EU store) as 'en'.
export const getSegmentLanguageTag = (
  store?: ReebeloStoreT | ReebeloB2bStoreT | string,
): SegmentLanguageTag => {
  const language =
    REEBELO_STORE_TO_LANGUAGE[store as ReebeloStoreT | ReebeloB2bStoreT];

  return language === 'fr' ||
    language === 'de' ||
    language === 'es' ||
    language === 'it' ||
    language === 'sv' ||
    language === 'nl' ||
    language === 'pt' ||
    language === 'fi'
    ? language
    : 'en';
};

export const REEBELO_LANGUAGE_LONG: Record<ReebeloLanguage, string> = {
  en: 'English',
  ko: 'Korean',
  zh: 'Chinese',
  fr: 'French',
  de: 'German',
  es: 'Spanish',
  it: 'Italian',
  sv: 'Swedish',
  nl: 'Dutch',
  pt: 'Portuguese',
  fi: 'Finnish',
};

export const REEBELO_STORE_TO_LOCALE: Record<
  ReebeloStoreT | ReebeloB2bStoreT,
  string
> = {
  'reebelo-us': 'en-US',
  'reebelo-au': 'en-AU',
  'reebelo-nz': 'en-NZ',
  quista: 'en-SG',
  'reebelo-tw': 'zh-TW',
  'reebelo-dev': 'en-SG',
  'reebelo-kr': 'ko',
  'reebelo-ca': 'en-CA',
  'reebelo-uk': 'en-GB',
  'reebelo-fr': 'fr-FR',
  'reebelo-de': 'de-DE',
  'reebelo-es': 'es-ES',
  'reebelo-it': 'it-IT',
  'reebelo-se': 'sv-SE',
  'reebelo-ie': 'en-IE',
  'reebelo-nl': 'nl-NL',
  'reebelo-at': 'de-AT',
  'reebelo-be': 'fr-BE',
  'reebelo-pt': 'pt-PT',
  'reebelo-fi': 'fi-FI',
  'reebelo-b2b': 'en-US',
  'reebelo-b2b-dev': 'en-US',
};

export const REEBELO_STORE_LOCATION_ID: Record<
  ReebeloStoreT | ReebeloB2bStoreT,
  number
> = {
  'reebelo-dev': 52150796454,
  quista: 31460884535,
  'reebelo-au': 49451335829,
  'reebelo-nz': 60921118876,
  'reebelo-kr': 63234015325,
  'reebelo-tw': 64223445150,
  'reebelo-us': 66716532919,
  'reebelo-ca': 86033301806,
  'reebelo-uk': 12345678901, // UK: To be updated when going live
  'reebelo-fr': 12345678901, // France: To be updated when going live
  'reebelo-de': 0, // DECISION NEEDED (marketing/ops): real Shopify inventory-location ID not sourced yet
  'reebelo-es': 0, // legacy/unused since migrating off Shopify - 0 is correct for every store
  'reebelo-it': 0, // legacy/unused since migrating off Shopify - 0 is correct for every store
  'reebelo-se': 0, // legacy/unused since migrating off Shopify - 0 is correct for every store
  'reebelo-ie': 0,
  'reebelo-nl': 0,
  'reebelo-at': 0,
  'reebelo-be': 0,
  'reebelo-pt': 0,
  'reebelo-fi': 0,
  'reebelo-b2b': 84536656169,
  'reebelo-b2b-dev': 88012751143,
};

export const REEBELO_CANADA_LOCATION_ID: Record<
  'CROSS_BORDER' | 'LOCAL',
  number
> =
  process.env.STAGE === 'prod'
    ? { LOCAL: 96020889902, CROSS_BORDER: 96020922670 }
    : {
        LOCAL: 70209110213,
        CROSS_BORDER: 70209175749,
      };

export const HANDLING_TIME_OPTIONS_BY_STORE: Record<ReebeloStoreT, number[]> = {
  quista: [0, 1, 2],
  'reebelo-au': [0, 1, 2],
  'reebelo-nz': [0, 1, 2],
  'reebelo-us': [0, 1],
  'reebelo-ca': [0, 1, 2],
  'reebelo-uk': [0, 1, 2],
  'reebelo-fr': [0, 1, 2],
  'reebelo-de': [0, 1, 2],
  'reebelo-es': [0, 1, 2],
  'reebelo-it': [0, 1, 2],
  'reebelo-se': [0, 1, 2],
  'reebelo-ie': [0, 1, 2],
  'reebelo-nl': [0, 1, 2],
  'reebelo-at': [0, 1, 2],
  'reebelo-be': [0, 1, 2],
  'reebelo-pt': [0, 1, 2],
  'reebelo-fi': [0, 1, 2],
  'reebelo-dev': [0, 1, 2],
  'reebelo-kr': [0, 1, 2],
  'reebelo-tw': [0, 1, 2],
};

export const EXPRESS_HANDLING_TIME_OPTIONS_BY_STORE: Record<
  ReebeloStoreT,
  number[]
> = {
  quista: [0, 1],
  'reebelo-au': [0, 1],
  'reebelo-nz': [0, 1],
  'reebelo-us': [0],
  'reebelo-ca': [0],
  'reebelo-uk': [0],
  'reebelo-fr': [0],
  'reebelo-de': [0],
  'reebelo-es': [0],
  'reebelo-it': [0],
  'reebelo-se': [0],
  'reebelo-ie': [0],
  'reebelo-nl': [0],
  'reebelo-at': [0],
  'reebelo-be': [0],
  'reebelo-pt': [0],
  'reebelo-fi': [0],
  'reebelo-dev': [0, 1],
  'reebelo-kr': [0],
  'reebelo-tw': [0],
};

export const REEBELO_STORES_MULTI_LOCATIONS: ReebeloStoreT[] = [
  'reebelo-dev',
  'reebelo-ca',
];

export const REEBELO_STORES_WITH_CARRIER: ReebeloStoreT[] = [
  'reebelo-dev',
  'reebelo-us',
];

export const ORDER_PREFIX: Record<ReebeloStoreT | ReebeloB2bStoreT, string> = {
  quista: '#',
  'reebelo-au': '#A',
  'reebelo-tw': '#TW',
  'reebelo-nz': '#NZ',
  'reebelo-kr': '#KR',
  'reebelo-us': '#US',
  'reebelo-ca': '#CA',
  'reebelo-uk': '#UK',
  'reebelo-fr': '#FR',
  'reebelo-de': '#DE',
  'reebelo-es': '#ES',
  'reebelo-it': '#IT',
  'reebelo-se': '#SE',
  'reebelo-ie': '#IE',
  'reebelo-nl': '#NL',
  'reebelo-at': '#AT',
  'reebelo-be': '#BE',
  'reebelo-pt': '#PT',
  'reebelo-fi': '#FI',
  'reebelo-dev': '#',
  'reebelo-b2b': '#B2B',
  'reebelo-b2b-dev': '#B2B',
};

// MsCheckout has it's own API keys and therefore it's own Shopify sales channel.
const MS_CHECKOUT_SALES_CHANNEL = 'MsCheckout';

export const REEBELO_STORE_TO_SALES_CHANNELS: Record<
  ReebeloStoreT | ReebeloB2bStoreT,
  string[]
> = {
  'reebelo-au': ['Online Store', 'Storefront v2', MS_CHECKOUT_SALES_CHANNEL],
  'reebelo-nz': ['Online Store', 'Storefront v2', MS_CHECKOUT_SALES_CHANNEL],
  quista: ['Online Store', 'Storefront V2', MS_CHECKOUT_SALES_CHANNEL], // intentionally upper case the v2 to match storefront app name on Shopify
  'reebelo-tw': ['Online Store'],
  'reebelo-dev': ['Online Store', 'Storefront v2', MS_CHECKOUT_SALES_CHANNEL],
  'reebelo-kr': ['Online Store', 'Storefront v2'],
  'reebelo-us': ['Online Store', 'Storefront v2', MS_CHECKOUT_SALES_CHANNEL],
  'reebelo-ca': ['Online Store', 'Storefront V2', MS_CHECKOUT_SALES_CHANNEL],
  'reebelo-uk': ['Online Store', 'Storefront V2', MS_CHECKOUT_SALES_CHANNEL],
  'reebelo-fr': ['Online Store', 'Storefront V2', MS_CHECKOUT_SALES_CHANNEL],
  'reebelo-de': ['Online Store', 'Storefront V2', MS_CHECKOUT_SALES_CHANNEL],
  'reebelo-es': ['Online Store', 'Storefront V2', MS_CHECKOUT_SALES_CHANNEL],
  'reebelo-it': ['Online Store', 'Storefront V2', MS_CHECKOUT_SALES_CHANNEL],
  'reebelo-se': ['Online Store', 'Storefront V2', MS_CHECKOUT_SALES_CHANNEL],
  'reebelo-ie': ['Online Store', 'Storefront V2', MS_CHECKOUT_SALES_CHANNEL],
  'reebelo-nl': ['Online Store', 'Storefront V2', MS_CHECKOUT_SALES_CHANNEL],
  'reebelo-at': ['Online Store', 'Storefront V2', MS_CHECKOUT_SALES_CHANNEL],
  'reebelo-be': ['Online Store', 'Storefront V2', MS_CHECKOUT_SALES_CHANNEL],
  'reebelo-pt': ['Online Store', 'Storefront V2', MS_CHECKOUT_SALES_CHANNEL],
  'reebelo-fi': ['Online Store', 'Storefront V2', MS_CHECKOUT_SALES_CHANNEL],
  'reebelo-b2b': ['Online Store', 'Storefront V2'],
  'reebelo-b2b-dev': ['Online Store', 'Storefront V2'],
};

export const REEBELO_US_SERVICE_PROVIDERS: string[] = [
  'Unlocked',
  'AT&T',
  'Verizon',
  'T-Mobile',
];

export enum ReebeloGtinRegion {
  APAC = 'Apac',
  USA = 'Usa',
  CA = 'Canada',
  UK = 'Uk',
  FR = 'France',
  DE = 'Germany',
  ES = 'Spain',
  IT = 'Italy',
  SE = 'Sweden',
  IE = 'Ireland',
  NL = 'Netherlands',
  AT = 'Austria',
  BE = 'Belgium',
  PT = 'Portugal',
  FI = 'Finland',
}
export const REEBELO_STORES_TO_REGIONS: Record<
  ReebeloStoreT | ReebeloB2bStoreT,
  ReebeloGtinRegion
> = {
  'reebelo-dev': ReebeloGtinRegion.USA,
  'reebelo-au': ReebeloGtinRegion.APAC,
  'reebelo-nz': ReebeloGtinRegion.APAC,
  quista: ReebeloGtinRegion.APAC,
  'reebelo-kr': ReebeloGtinRegion.APAC,
  'reebelo-tw': ReebeloGtinRegion.APAC,
  'reebelo-us': ReebeloGtinRegion.USA,
  'reebelo-ca': ReebeloGtinRegion.CA,
  'reebelo-uk': ReebeloGtinRegion.UK,
  'reebelo-b2b': ReebeloGtinRegion.USA,
  'reebelo-b2b-dev': ReebeloGtinRegion.USA,
  'reebelo-fr': ReebeloGtinRegion.FR,
  'reebelo-de': ReebeloGtinRegion.DE,
  'reebelo-es': ReebeloGtinRegion.ES,
  'reebelo-it': ReebeloGtinRegion.IT,
  'reebelo-se': ReebeloGtinRegion.SE,
  'reebelo-ie': ReebeloGtinRegion.IE,
  'reebelo-nl': ReebeloGtinRegion.NL,
  'reebelo-at': ReebeloGtinRegion.AT,
  'reebelo-be': ReebeloGtinRegion.BE,
  'reebelo-pt': ReebeloGtinRegion.PT,
  'reebelo-fi': ReebeloGtinRegion.FI,
};

export enum ReebeloVendorsInvetoryOrigins {
  HONG_KONG = 'Hong Kong',
  NEW_ZEALAND = 'New Zealand',
  AUSTRALIA = 'Australia',
  USA = 'United States',
  SINGAPORE = 'Singapore',
  TAIWAN = 'Taiwan',
  KOREA = 'Korea',
  CANADA = 'Canada',
  UK = 'United Kingdom',
  FRANCE = 'France',
  GERMANY = 'Germany',
  SPAIN = 'Spain',
  ITALY = 'Italy',
  SWEDEN = 'Sweden',
  IRELAND = 'Ireland',
  NETHERLANDS = 'Netherlands',
  AUSTRIA = 'Austria',
  BELGIUM = 'Belgium',
  PORTUGAL = 'Portugal',
  FINLAND = 'Finland',
}

export const REEBELO_STORE_TO_INVENTORY_ORIGIN: Record<
  ReebeloStoreT,
  ReebeloVendorsInvetoryOrigins
> = {
  'reebelo-au': ReebeloVendorsInvetoryOrigins.AUSTRALIA,
  'reebelo-nz': ReebeloVendorsInvetoryOrigins.NEW_ZEALAND,
  'reebelo-dev': ReebeloVendorsInvetoryOrigins.SINGAPORE,
  'reebelo-tw': ReebeloVendorsInvetoryOrigins.TAIWAN,
  'reebelo-kr': ReebeloVendorsInvetoryOrigins.KOREA,
  'reebelo-us': ReebeloVendorsInvetoryOrigins.USA,
  'reebelo-ca': ReebeloVendorsInvetoryOrigins.CANADA,
  'reebelo-uk': ReebeloVendorsInvetoryOrigins.UK,
  'reebelo-fr': ReebeloVendorsInvetoryOrigins.FRANCE,
  'reebelo-de': ReebeloVendorsInvetoryOrigins.GERMANY,
  'reebelo-es': ReebeloVendorsInvetoryOrigins.SPAIN,
  'reebelo-it': ReebeloVendorsInvetoryOrigins.ITALY,
  'reebelo-se': ReebeloVendorsInvetoryOrigins.SWEDEN,
  'reebelo-ie': ReebeloVendorsInvetoryOrigins.IRELAND,
  'reebelo-nl': ReebeloVendorsInvetoryOrigins.NETHERLANDS,
  'reebelo-at': ReebeloVendorsInvetoryOrigins.AUSTRIA,
  'reebelo-be': ReebeloVendorsInvetoryOrigins.BELGIUM,
  'reebelo-pt': ReebeloVendorsInvetoryOrigins.PORTUGAL,
  'reebelo-fi': ReebeloVendorsInvetoryOrigins.FINLAND,
  quista: ReebeloVendorsInvetoryOrigins.SINGAPORE,
};

/**
 * Countries a vendor may set as a return address. Deliberately wider than
 * `REEBELO_COUNTRY_CODE`, which lists the countries Reebelo *sells* in — a
 * seller can sit outside every market it lists into (RT-16873: an Irish
 * seller listing into France).
 *
 * Curated rather than the full ISO 3166-1 list: the code stored here is
 * matched exactly by `formatReturnAddressBasedOnRegion` and the carrier
 * payloads downstream, so an unreachable country is worse than a missing one.
 */
export const RETURN_ADDRESS_COUNTRIES: Record<string, string> = {
  AT: 'Austria',
  AU: 'Australia',
  BE: 'Belgium',
  BG: 'Bulgaria',
  CA: 'Canada',
  CH: 'Switzerland',
  CY: 'Cyprus',
  CZ: 'Czechia',
  DE: 'Germany',
  DK: 'Denmark',
  EE: 'Estonia',
  ES: 'Spain',
  FI: 'Finland',
  FR: 'France',
  GB: 'United Kingdom',
  GR: 'Greece',
  HR: 'Croatia',
  HU: 'Hungary',
  IE: 'Ireland',
  IS: 'Iceland',
  IT: 'Italy',
  LI: 'Liechtenstein',
  LT: 'Lithuania',
  LU: 'Luxembourg',
  LV: 'Latvia',
  MT: 'Malta',
  NL: 'Netherlands',
  NO: 'Norway',
  NZ: 'New Zealand',
  PL: 'Poland',
  PT: 'Portugal',
  RO: 'Romania',
  SE: 'Sweden',
  SG: 'Singapore',
  SI: 'Slovenia',
  SK: 'Slovakia',
  US: 'United States',
};

// Only used for returns atm. Absence of a country is meaningful: it makes
// that country's return-address state optional and free-text — which is why
// `UK` is inert, the code stored for the United Kingdom being `GB`.
export const REEBELO_COUNTRY_CODE_TO_STATES: Record<string, Array<string>> = {
  AU: ['NSW', 'VIC', 'ACT', 'QLD', 'SA', 'WA', 'TAS', 'NT'],
  NZ: [
    'Auckland',
    'Bay of Plenty',
    'Canterbury',
    'Christchurch',
    'Chatham Islands Territory',
    'Gisborne',
    'Greater Wellington',
    "Hawke's Bay",
    'Manawatū-Whanganui',
    'Marlborough',
    'Nelson',
    'Northland',
    'Ngahinapouri',
    'Otago',
    'Paihia',
    'Southland',
    'Taranaki',
    'Tauranga',
    'Tasman',
    'Waikato',
    'West Coast',
  ],
  SG: ['SG'],
  DEV: ['SG'],
  US: [
    'AL',
    'AK',
    'AZ',
    'AR',
    'CA',
    'CO',
    'CT',
    'DE',
    'FL',
    'GA',
    'HI',
    'ID',
    'IL',
    'IN',
    'IA',
    'KS',
    'KY',
    'LA',
    'ME',
    'MD',
    'MA',
    'MI',
    'MN',
    'MS',
    'MO',
    'MT',
    'NE',
    'NV',
    'NH',
    'NJ',
    'NM',
    'NY',
    'NC',
    'ND',
    'OH',
    'OK',
    'OR',
    'PA',
    'RI',
    'SC',
    'SD',
    'TN',
    'TX',
    'UT',
    'VT',
    'VA',
    'WA',
    'WV',
    'WI',
    'WY',
  ],
  CA: [
    'AB',
    'BC',
    'MB',
    'NB',
    'NL',
    'NT',
    'NS',
    'NU',
    'ON',
    'PE',
    'QC',
    'SK',
    'YT',
  ],
  UK: [
    'Bedfordshire',
    'Berkshire',
    'Bristol',
    'Buckinghamshire',
    'Cambridgeshire',
    'Cheshire',
    'Cornwall',
    'County Durham',
    'Cumbria',
    'Derbyshire',
    'Devon',
    'Dorset',
    'East Riding of Yorkshire',
    'East Sussex',
    'Essex',
    'Gloucestershire',
    'Greater London',
    'Greater Manchester',
    'Hampshire',
    'Herefordshire',
    'Hertfordshire',
    'Isle of Wight',
    'Kent',
    'Lancashire',
    'Leicestershire',
    'Lincolnshire',
    'Merseyside',
    'Norfolk',
    'North Somerset',
    'North Yorkshire',
    'Northamptonshire',
    'Northumberland',
    'Nottinghamshire',
    'Oxfordshire',
    'Rutland',
    'Shropshire',
    'Somerset',
    'South Gloucestershire',
    'South Yorkshire',
    'Staffordshire',
    'Suffolk',
    'Surrey',
    'Tyne & Wear',
    'Warwickshire',
    'West Midlands',
    'West Sussex',
    'West Yorkshire',
    'Wiltshire',
    'Worcestershire',
    'Aberdeenshire',
    'Angus',
    'Argyll & Bute',
    'Ayrshire',
    'Banffshire',
    'Berwickshire',
    'Borders',
    'Caithness',
    'Clackmannanshire',
    'Dumfries & Galloway',
    'Dunbartonshire',
    'East Ayrshire',
    'East Dunbartonshire',
    'East Lothian',
    'East Renfrewshire',
    'Fife',
    'Highland',
    'Inverclyde',
    'Kincardineshire',
    'Midlothian',
    'Moray',
    'North Ayrshire',
    'North Lanarkshire',
    'Orkney',
    'Perth & KInross',
    'Renfrewshire',
    'Shetland',
    'South Ayrshire',
    'South Lanarkshire',
    'Stirlingshire',
    'West Dunbartonshire',
    'West Lothian',
    'Western Isles',
    'Blaenau Gwent',
    'Bridgend',
    'Caerphilly',
    'Cardiff',
    'Carmarthenshire',
    'Ceredigion',
    'Conwy',
    'Denbighshire',
    'Flintshire',
    'Gwynedd',
    'Isle of Anglesey',
    'Merthyr Tydfil',
    'Monmouthshire',
    'Neath Port Talbot',
    'Newport',
    'Pembrokeshire',
    'Powys',
    'Rhondda Cynon Taff',
    'Swansea',
    'Torfaen',
    'Vale of Glamorgan',
    'Wrexham',
    'Antrim',
    'Armagh',
    'Down',
    'Fermanagh',
    'Londonderry',
    'Tyrone',
  ],
  FR: [
    'Auvergne-Rhône-Alpes',
    'Bourgogne-Franche-Comté',
    'Bretagne',
    'Centre-Val de Loire',
    'Corse',
    'Grand Est',
    'Hauts-de-France',
    'Île-de-France',
    'Normandie',
    'Nouvelle-Aquitaine',
    'Occitanie',
    'Pays de la Loire',
    "Provence-Alpes-Côte d'Azur",
    'Guadeloupe',
    'Martinique',
    'Guyane',
    'La Réunion',
    'Mayotte',
  ],
  AT: [
    'Burgenland',
    'Kärnten',
    'Niederösterreich',
    'Oberösterreich',
    'Salzburg',
    'Steiermark',
    'Tirol',
    'Vorarlberg',
    'Wien',
  ],
  BE: [
    'Antwerpen',
    'Brabant wallon',
    'Brussels-Capital Region',
    'Hainaut',
    'Liège',
    'Limburg',
    'Luxembourg',
    'Namur',
    'Oost-Vlaanderen',
    'Vlaams-Brabant',
    'West-Vlaanderen',
  ],
  PT: [
    'Aveiro',
    'Beja',
    'Braga',
    'Bragança',
    'Castelo Branco',
    'Coimbra',
    'Évora',
    'Faro',
    'Guarda',
    'Leiria',
    'Lisboa',
    'Portalegre',
    'Porto',
    'Santarém',
    'Setúbal',
    'Viana do Castelo',
    'Vila Real',
    'Viseu',
  ],
  FI: [
    'Etelä-Karjala',
    'Etelä-Pohjanmaa',
    'Etelä-Savo',
    'Kainuu',
    'Kanta-Häme',
    'Keski-Pohjanmaa',
    'Keski-Suomi',
    'Kymenlaakso',
    'Lappi',
    'Pirkanmaa',
    'Pohjanmaa',
    'Pohjois-Karjala',
    'Pohjois-Pohjanmaa',
    'Pohjois-Savo',
    'Päijät-Häme',
    'Satakunta',
    'Uusimaa',
    'Varsinais-Suomi',
  ],
  DE: [
    'Baden-Württemberg',
    'Bayern',
    'Berlin',
    'Brandenburg',
    'Bremen',
    'Hamburg',
    'Hessen',
    'Mecklenburg-Vorpommern',
    'Niedersachsen',
    'Nordrhein-Westfalen',
    'Rheinland-Pfalz',
    'Saarland',
    'Sachsen',
    'Sachsen-Anhalt',
    'Schleswig-Holstein',
    'Thüringen',
  ],
  ES: [
    'Andalucía',
    'Aragón',
    'Asturias',
    'Islas Baleares',
    'Canarias',
    'Cantabria',
    'Castilla-La Mancha',
    'Castilla y León',
    'Cataluña',
    'Extremadura',
    'Galicia',
    'La Rioja',
    'Comunidad de Madrid',
    'Región de Murcia',
    'Comunidad Foral de Navarra',
    'País Vasco',
    'Comunidad Valenciana',
  ],
};
export const REEBELO_COUNTRY_TAXES: Record<string, number> = {
  AU: 1.1,
  SG: 1,
  NZ: 1.15,
  KR: 1.1,
  TW: 1.05,
  GB: 1.2, // UK rate
};
export const REEBELO_CROSS_BORDER_TAXES: Record<string, number> = {
  AU: 11,
  NZ: 7.666,
};

export const REEBELO_SALES_THRESHOLD: Record<string, number> = {
  AU: 1099.99,
  NZ: 1149.99,
};

export const REEBELO_STORES_WITHOUT_GST: Array<ReebeloStoreT> = [
  'reebelo-us',
  'reebelo-ca',
  'reebelo-dev',
];

export const REEBELO_SALES_CHANNEL_PREFIX = 'SALES_CHANNEL';
export const REEBELO_EXTERNAL_ORDER_ID_PREFIX = 'OID';
export const REEBELO_EXTERNAL_PURCHASE_ORDER_NUMBER_PREFIX = 'PON';
export enum ReebeloSalesChannelT {
  REEBELO = 'Reebelo',
  WALMART = 'Walmart',
  WALMART_CA = 'WalmartCA',
  MYDEAL = 'MyDeal',
  NEWEGG = 'Newegg',
  AMAZON = 'Amazon',
  WISH = 'Wish',
  MERCARI = 'Mercari',
  TIKTOK = 'Tiktok',
  BUYBACK = 'Buyback',
}
export const REEBELO_CANADA_MARKETPLACE_REGIONAL_TAX: Record<string, number> = {
  ON: 0.13,
  NB: 0.15,
  NL: 0.15,
  NS: 0.15,
  PEI: 0.15,
  AB: 0.05,
  NWT: 0.05,
  YT: 0.05,
  NU: 0.05,
  QC: 0.14975,
  BC: 0.12,
  SK: 0.11,
  MB: 0.12,
  USA: 0,
  CB: 0,
};

export enum ReebeloBatteryHealth {
  BH_80 = '80',
  BH_90 = '90',
  BH_100 = '100',
}

export const SSMBUCKET_ES_SEARCH = 'es-search';
export const SSMBUCKET_SHIPPING = 'shipping';
export const SSMBUCKET_SLACK = 'slack';
export const SSMBUCKET_ZENDESK = 'zendesk';
export const SSMBUCKET_GOOGLE = 'google';
export const SSMBUCKET_MS_ENDPOINT = 'ms-endpoint';
export const SSMBUCKET_COBALT = 'cobalt';
export const SSMBUCKET_FEEDS = 'feeds';
export const SSMBUCKET_BRAZE = 'braze';
export const SSMBUCKET_METABASE = 'metabase';
export const SSMBUCKET_MS_SHIPPING = 'ms_shipping';
export const SSMBUCKET_FINANCE = 'finance';

export type SSMBucket =
  | 'reebelo-dev'
  | 'reebelo-au'
  | 'reebelo-kr'
  | 'reebelo-nz'
  | 'quista'
  | 'reebelo-tw'
  | 'reebelo-us'
  | 'reebelo-ca'
  | 'reebelo-uk'
  | 'reebelo-fr'
  | 'reebelo-de'
  | 'reebelo-es'
  | 'reebelo-it'
  | 'reebelo-se'
  | 'reebelo-ie'
  | 'reebelo-nl'
  | 'reebelo-at'
  | 'reebelo-be'
  | 'reebelo-pt'
  | 'reebelo-fi'
  | 'es-search'
  | 'slack'
  | 'zendesk'
  | 'google'
  | 'auth'
  | 'aftership'
  | 'shipping'
  | 'ad-lucent'
  | 'cobalt'
  | 'feeds'
  | 'braze'
  | 'metabase'
  | 'feature_flag'
  | 'parcel_port'
  | 'posthog'
  | typeof SSMBUCKET_MS_ENDPOINT
  | typeof SSMBUCKET_MS_SHIPPING
  | typeof SSMBUCKET_FINANCE;

export enum RC_DURATION {
  '24_MONTH' = '24',
}

export enum ReebeloSimSlot {
  DS = 'Dual Sim',
  SS = 'Single Sim',
  ES = 'Dual eSim',
  SSES = 'Single Sim + eSim',
  TPSES = '2 Physical Sim + eSim',
}
